import { ModeBadge } from '../badges/ModeBadge';
import { PresenceIndicator } from '../badges/PresenceIndicator';
import { SilentMonitoringIndicator } from '../badges/SilentMonitoringIndicator';

import type { ConversationMirrorVM } from '../../view-models';
import type { MonitoringMode } from '../badges/SilentMonitoringIndicator';
import type React from 'react';

export interface ConversationMirrorProps {
  vm: ConversationMirrorVM;
  monitoringMode: MonitoringMode;
  supervisorName: string;
}

/**
 * ConversationMirror — espelho fiel read-only da tela do atendente (coluna esq Tela 2).
 * Convertido de design-references/brief-3/shared/cards-3.jsx:626-691.
 *
 * Read-only: o supervisor NÃO compõe mensagens (o composer do mockup vira um
 * estado textual). Markup `.msg` próprio do espelho (CSS já portado em
 * supervisor-compounds.css) — distinto do MessageBubble do atendimento.
 */
export function ConversationMirror({
  vm,
  monitoringMode,
  supervisorName,
}: ConversationMirrorProps): React.JSX.Element {
  const { header, messages, composer } = vm;
  return (
    <section className="mirror">
      <header className="mirror__head">
        <div className="mirror__avatar">{header.customerInitials}</div>
        <div className="mirror__head-info">
          <div className="mirror__head-line">
            <b>{header.customerName}</b>
            {header.channelIdentifier !== null && (
              <>
                <span className="mirror__sep" aria-hidden="true" />
                <span className="mono mirror__phone">{header.channelIdentifier}</span>
              </>
            )}
          </div>
          <div className="mirror__head-meta">
            <span className="badge badge--neutral">{header.channelLabel}</span>
            {header.bondLabel !== null && (
              <span className="bond-kind bond-kind--commercial">{header.bondLabel}</span>
            )}
            {header.classificationLabel !== null && (
              <span className="badge badge--neutral">{header.classificationLabel}</span>
            )}
            {header.mode !== null && <ModeBadge mode={header.mode} compact />}
          </div>
        </div>
        <div className="mirror__head-right">
          <PresenceIndicator presence={header.attendantPresence} />
          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-3)' }}>
            {header.attendantName}
          </span>
        </div>
      </header>

      <SilentMonitoringIndicator supervisorName={supervisorName} mode={monitoringMode} />

      <div className="mirror__messages">
        {messages.map((m) => (
          <div key={m.id} className={'msg msg--' + m.who}>
            {m.who === 'customer' ? (
              <div className="msg__avatar msg__avatar--customer">{header.customerInitials}</div>
            ) : (
              <div className="msg__avatar msg__avatar--attendant">{header.attendantInitials}</div>
            )}
            <div className="msg__bubble">
              <div className="msg__text">{m.text}</div>
              <div className="msg__time mono">{m.atLabel}</div>
            </div>
          </div>
        ))}
        {composer !== null && (
          <div className="msg msg--composing">
            <div className="msg__avatar msg__avatar--attendant">{composer.attendantInitials}</div>
            <div className="msg__bubble msg__bubble--composing">
              <div className="msg__composing-label">{composer.attendantName} está digitando…</div>
              <div className="msg__text">
                {composer.text}
                <span className="msg__cursor" aria-hidden="true">
                  ▍
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      <footer className="mirror__composer">
        <div className="mirror__composer-state">
          {monitoringMode === 'visible-to-both' ? (
            <span style={{ color: 'var(--brand-subtle-fg)' }}>
              Modo público — você pode enviar mensagens ao cliente
            </span>
          ) : (
            <span style={{ color: 'var(--fg-3)' }}>
              Somente leitura · supervisor não pode compor mensagens neste modo
            </span>
          )}
        </div>
      </footer>
    </section>
  );
}
